import { computed, ref } from 'vue'

import { useRoute, useRouter } from 'vue-router/composables'
import { EditorModule } from '@/store/modules/editor'
import { CourseModule } from '@/store/modules/course'
import { UserModule } from '@/store/modules/user'

// Editor routes that should maintain editor mode
const EDITOR_ROUTES = ['EditorDashboard', 'CourseInfoEditor', 'CourseModule', 'Lesson']

// Routes that should enable editor mode
const ENABLE_EDITOR_ROUTES = ['EditorDashboard', 'CourseInfoEditor']

// Routes that should reset course state when entering editor mode
const RESET_COURSE_ROUTES = ['EditorDashboard', 'CourseInfoEditor']

export default function useEditorModeManager() {
  const route = useRoute()
  const router = useRouter()
  const hasHandledInitialRoute = ref(false)

  const isEditorRoute = computed(() => EDITOR_ROUTES.includes(route.name as string))
  const shouldEnableEditorMode = computed(() => ENABLE_EDITOR_ROUTES.includes(route.name as string))
  const shouldResetCourseState = computed(() => RESET_COURSE_ROUTES.includes(route.name as string))

  const enableEditorMode = () => {
    if (!UserModule.isCompanyEditor) {
      router.push('/')
      return
    }

    EditorModule.setEditMode(true)

    if (shouldResetCourseState.value) {
      CourseModule.setCourse(null)
      CourseModule.setCourseBundle(null)
    }
  }

  const enableEditorModeForRoute = (routeName: string, fromRouteName?: string) => {
    if (!UserModule.isCompanyEditor) {
      router.push('/')
      return
    }

    EditorModule.setEditMode(true)

    // Reset course state if coming from a non-editor route
    if (RESET_COURSE_ROUTES.includes(routeName) && (!fromRouteName || !EDITOR_ROUTES.includes(fromRouteName))) {
      CourseModule.setCourse(null)
      CourseModule.setCourseBundle(null)
    }
  }

  const disableEditorMode = () => {
    EditorModule.setEditMode(false)
  }

  const shouldMaintainEditorMode = (toRouteName: string, fromRouteName: string): boolean => {
    // If coming from CourseInfoEditor and going to CourseModule or Lesson, maintain editor mode
    if (fromRouteName === 'CourseInfoEditor' && ['CourseModule', 'Lesson'].includes(toRouteName)) {
      return true
    }

    // If going to any editor route from another editor route, maintain editor mode
    if (EDITOR_ROUTES.includes(fromRouteName) && EDITOR_ROUTES.includes(toRouteName)) {
      return true
    }

    return false
  }

  const shouldDisableEditorMode = (toRouteName: string, fromRouteName: string): boolean => {
    if (!EditorModule.editMode) {
      return false
    }

    // If leaving EditorDashboard to any non-editor route, disable editor mode
    if (fromRouteName === 'EditorDashboard' && !EDITOR_ROUTES.includes(toRouteName)) {
      return true
    }

    // If leaving CourseInfoEditor to non-editor routes (except CourseModule/Lesson), disable editor mode
    if (fromRouteName === 'CourseInfoEditor' && !shouldMaintainEditorMode(toRouteName, fromRouteName)) {
      return true
    }

    // If leaving CourseModule/Lesson to non-editor routes, disable editor mode
    if (['CourseModule', 'Lesson'].includes(fromRouteName) && !EDITOR_ROUTES.includes(toRouteName)) {
      return true
    }

    return false
  }

  const handleRouteChange = (toRouteName: string, fromRouteName: string) => {
    // Reset the initial route flag since we have a real route change
    hasHandledInitialRoute.value = false

    // Enable editor mode for specific routes
    if (ENABLE_EDITOR_ROUTES.includes(toRouteName) && !EditorModule.editMode) {
      enableEditorModeForRoute(toRouteName, fromRouteName)
      return
    }

    // Maintain editor mode for specific transitions
    if (shouldMaintainEditorMode(toRouteName, fromRouteName)) {
      return
    }

    // Disable editor mode for specific transitions
    if (shouldDisableEditorMode(toRouteName, fromRouteName)) {
      disableEditorMode()

      CourseModule.setCourse(null)
      CourseModule.setCourseBundle(null)

      return
    }
  }

  const handleInitialRoute = (routeName: string) => {
    // Only handle initial route once to prevent conflicts with route changes
    if (hasHandledInitialRoute.value) {
      return
    }

    // Handle initial load on editor routes
    if (ENABLE_EDITOR_ROUTES.includes(routeName) && !EditorModule.editMode) {
      enableEditorModeForRoute(routeName, '')
      hasHandledInitialRoute.value = true
      return
    }
  }

  return {
    isEditorRoute,
    shouldEnableEditorMode,
    shouldResetCourseState,
    enableEditorMode,
    enableEditorModeForRoute,
    disableEditorMode,
    shouldMaintainEditorMode,
    shouldDisableEditorMode,
    handleRouteChange,
    handleInitialRoute,
  }
}
